import { Icon, type IconNameType } from "@src/components/Icon";
import { Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import type { Href } from "expo-router";
import { Pressable } from "react-native";
import Animated, {
  FadeIn,
  FadeOut,
  LinearTransition,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from "react-native-reanimated";

interface SidepanelAnimatedActionItemProps {
  label: string;
  icon: IconNameType;
  onPress: () => void;
  href?: Href;
  externalHref?: string;
  collapsed?: boolean;
  colorToken?: "icon" | "danger";
  isSelected?: boolean;
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

export function SidepanelAnimatedActionItem({
  label,
  icon,
  onPress,
  isSelected,
  collapsed = false,
  colorToken = "icon",
}: SidepanelAnimatedActionItemProps) {
  const { theme } = useUnistyles();

  const getIconColor = () => {
    if (colorToken === "danger") {
      return theme.colors.danger[400];
    }
    if (isSelected) {
      return theme.colors.interactive.buttons.hover_background;
    }
    return theme.colors.icon;
  };

  // We have to animate the pressable background, icon color when isSelected
  const isSelectedValue = useDerivedValue(() => {
    return withTiming(isSelected ? 1 : 0, { duration: 300 });
  });

  const backgroundColor = useDerivedValue(() => {
    return withTiming(isSelectedValue.value ? theme.colors.background : "transparent", {
      duration: 300,
    });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    backgroundColor: backgroundColor.value,
  }));

  if (collapsed) {
    return (
      <AnimatedPressable onPress={onPress} style={[styles.container, animatedStyle, { justifyContent: "center" }]}>
        {isSelected && <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.selectedDot} />}
        <Icon name={icon} size={theme.spacing["5xl"]} color={getIconColor()} />
      </AnimatedPressable>
    );
  }

  return (
    <AnimatedPressable onPress={onPress} style={[styles.container, animatedStyle]}>
      {isSelected && <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.selectedDot} />}
      <Animated.View layout={LinearTransition} style={styles.row}>
        <Icon name={icon} size={theme.spacing["5xl"]} color={getIconColor()} />
        <Text textStyle="sm/regular" color={colorToken === "danger" ? "danger" : "text"}>
          {label}
        </Text>
      </Animated.View>
    </AnimatedPressable>
  );
}

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingVertical: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
  },
  selectedDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: theme.colors.interactive.buttons.hover_background,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
  },
}));
